/**
 * <AIBadge /> — the AI marker. 12px sparkle in Solvait Brand Teal.
 *
 * Per DESIGN_SYSTEM.md §6 "AI-Element Conventions":
 *   - Icon: 12px sparkle (Lucide Sparkles) in --brand-teal. The
 *     sparkle ✦ is the one place brand teal is the foreground — an
 *     icon, so it's exempt from the teal-fails-AA-for-text rule.
 *   - Position: top-right of the element OR inline-leading on text.
 *   - Hover/click: reveals "AI did this — click to inspect" tooltip
 *     with a link to the AI Activity Log entry.
 *
 * Every UI element produced or driven by the AI carries this marker.
 * The Activity Log link href is provided by the caller via
 * `activityLogHref`; if omitted, the badge becomes display-only (no
 * link, just hover tooltip).
 */

import { Sparkles } from "lucide-react";
import type { ReactNode } from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";

export interface AIBadgeProps {
  /** Optional override for the tooltip text. Defaults to ai.marker_tooltip. */
  tooltipText?: string;
  /** Optional href to the AI Activity Log entry for this action. */
  activityLogHref?: string;
  /** Render inline-leading (default) or as a corner-positioned absolute badge. */
  position?: "inline" | "corner";
  /** Pixel size of the icon. Default 12. */
  size?: number;
  className?: string;
}

export function AIBadge({
  tooltipText,
  activityLogHref,
  position = "inline",
  size = 12,
  className = "",
}: AIBadgeProps): ReactNode {
  const { t } = useTranslation();
  const label = tooltipText ?? t("ai.marker_tooltip");

  const positionClasses =
    position === "corner"
      ? "absolute top-1 end-1"
      : "inline-flex items-center";

  const iconClasses = [
    "text-brand-teal",
    "shrink-0",
    positionClasses,
    className,
  ]
    .filter(Boolean)
    .join(" ");

  // The actual visual.
  const icon = (
    <Sparkles
      aria-label={label}
      role="img"
      size={size}
      strokeWidth={2.25}
      className={iconClasses}
    />
  );

  // Wrap in a Link if an Activity Log href is provided. The link
  // carries the title attribute so hover gives the tooltip without
  // needing a portal.
  if (activityLogHref) {
    return (
      <Link
        to={activityLogHref}
        title={label}
        aria-label={label}
        className="inline-flex items-center focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ai/40 rounded-sm"
      >
        {icon}
      </Link>
    );
  }

  // Display-only — wrap with a span carrying the title for hover.
  return (
    <span title={label} className="inline-flex items-center">
      {icon}
    </span>
  );
}
